import { useInRecordSection, useSectionWideClass } from 'components/section-edit/SectionEdit';
import { Textarea } from 'components/ui/textarea';
import useAuth from 'hooks/useAuth';
import { useRef } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { handleAutofillOnClick, trimWithTrailingNewline } from 'utils/autofillUtil';
import { cn } from 'utils/cn';
import ReadonlyField from '../rhf-other/ReadonlyField';
import { CommonInputProps } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useOwnPlaceholder } from './newDesignInput';
import { useMemoizedRules } from './useMemoizedRules';

interface Props<T extends FieldValues> extends CommonInputProps<T> {
    maxRows?: number;
    minRows?: number;
    maxLength?: number;
    readonly?: boolean;
    textAreaReadonly?: boolean;
    decimalScale?: number;
    onBlur?: (value: string | null) => void;
    enableAutofill?: boolean;
    keepIndentation?: boolean;
}

/** One line of the textarea, so `minRows`/`maxRows` can bound its auto-sizing in `em`. */
const ROW_HEIGHT_EM = 1.4375;
/** The control's own vertical padding (`py-2` twice), in `em` at the 14px text size. */
const VERTICAL_PADDING_EM = 1.15;

const rowsToEm = (rows: number) => `${(rows * ROW_HEIGHT_EM + VERTICAL_PADDING_EM).toFixed(4)}em`;

/**
 * A text area in a record section: one line of text in the cell's 36px box to start with, growing with its text
 * alone. So it has no resize grip, and its placeholder stays on one line, cut at the box's end: the box sizes itself
 * to its content, and a placeholder that wrapped would make an empty field taller than the read view's cell.
 */
export const SECTION_TEXT_AREA_CLASS = 'min-h-0 resize-none py-[7px] placeholder-shown:truncate';

const TextAreaInput = <T extends FieldValues>({
    name,
    disabled,
    tooltip,
    required,
    label,
    shouldTranslateLabel,
    rightLabelComponent,
    wrapperClassName,
    placeholder,
    shouldTranslatePlaceholder = true,
    validate,
    onBlur,
    control,
    maxRows = 10,
    minRows,
    maxLength,
    readonly = false,
    textAreaReadonly = false,
    decimalScale,
    enableAutofill = false,
    keepIndentation = false,
}: Props<T>) => {
    const rules = useMemoizedRules({ required, validate });
    const ownPlaceholder = useOwnPlaceholder(placeholder, label, shouldTranslatePlaceholder);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const { user } = useAuth();
    // A record section in edit draws the text where its read view has it: the whole row for a stored text and one
    // cell without, one line of text in the cell's 36px box to start with, whatever `minRows` asks, and every
    // line of a long one, as the read view shows them all.
    const inSection = useInRecordSection();
    const wideClass = useSectionWideClass(control, name);
    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({
                field: { ref, value, onChange: innerOnChange, onBlur: innerOnBlur, ...field },
                fieldState: { error },
            }) => {
                const isEmpty = value === undefined || value === null || value === '';
                const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
                    if (!keepIndentation) return;

                    e.preventDefault();
                    const pastedText = e.clipboardData.getData('text/plain');

                    const textarea = inputRef.current;
                    if (!textarea) return;

                    const start = textarea.selectionStart ?? 0;
                    const end = textarea.selectionEnd ?? 0;
                    const currentValue = value || '';

                    const newValue = currentValue.substring(0, start) + pastedText + currentValue.substring(end);

                    innerOnChange(newValue);

                    requestAnimationFrame(() => {
                        const newCursorPos = start + pastedText.length;
                        textarea.setSelectionRange(newCursorPos, newCursorPos);
                    });
                };

                return (
                    <InputWrapper
                        name={name}
                        label={label}
                        shouldTranslateLabel={shouldTranslateLabel}
                        tooltip={tooltip}
                        required={required}
                        isEmpty={isEmpty}
                        readonly={!!readonly}
                        rightLabelComponent={rightLabelComponent}
                        hasErrors={!!error}
                        error={readonly && !textAreaReadonly ? undefined : error}
                        className={cn(wideClass, wrapperClassName)}
                    >
                        {readonly && !textAreaReadonly ? (
                            <ReadonlyField value={value} decimalScale={decimalScale} />
                        ) : (
                            <Textarea
                                ref={(element) => {
                                    ref(element);
                                    inputRef.current = element;
                                }}
                                {...field}
                                className={cn('bg-paper min-h-0', inSection && SECTION_TEXT_AREA_CLASS)}
                                style={{
                                    minHeight: minRows && !inSection ? rowsToEm(minRows) : undefined,
                                    maxHeight: inSection ? undefined : rowsToEm(maxRows),
                                }}
                                onChange={(e) => {
                                    innerOnChange(e.target.value);
                                }}
                                onClick={() => {
                                    if (enableAutofill) {
                                        handleAutofillOnClick(inputRef, value || '', user?.name, innerOnChange);
                                    }
                                }}
                                onBlur={() => {
                                    innerOnBlur();
                                    const trimmedValue = trimWithTrailingNewline(value);
                                    innerOnChange(trimmedValue);
                                    if (onBlur) {
                                        onBlur(trimmedValue);
                                    }
                                }}
                                onPaste={handlePaste}
                                value={value || ''}
                                id={name}
                                disabled={disabled}
                                required={required}
                                aria-invalid={!!error}
                                maxLength={maxLength}
                                readOnly={textAreaReadonly}
                                data-form-type="other"
                                data-lpignore="true"
                                placeholder={ownPlaceholder}
                            />
                        )}
                    </InputWrapper>
                );
            }}
        />
    );
};

export default TextAreaInput;
